
// @section: portfolio-main
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import {
  Mail, Phone, MapPin, Award, BookOpen, Briefcase,
  ChevronDown, ExternalLink, Star, TrendingUp, Users, BarChart2,
  Database, Globe, Menu, X, CheckCircle
} from "lucide-react";
import { FaLinkedin } from "react-icons/fa";

/* ─── CONSTANTS ─── */
const PORTRAIT = "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-17/prod_agent_0c71c832-7dfc-472f-a640-92bae059de7a/Gemini_Generated_Image_qlmsb3qlmsb3qlms_884a43d048bd48b18d1fbf82c34beb1c.png";
const NAVY = "#1e2a4a";
const GOLD = "#c9972b";
const NAVY_LIGHT = "#253560";
const TEAL = "#2a7a8c";

/* ─── DATA ─── */
const skillsRadar = [
  { skill: "Power BI", score: 97 },
  { skill: "Data Analytics", score: 92 },
  { skill: "Procurement", score: 95 },
  { skill: "Supply Chain", score: 88 },
  { skill: "SAP ERP", score: 82 },
  { skill: "ESG/BRSR", score: 80 },
];

const technicalBars = [
  { name: "Power BI (DAX/M)", level: 97, color: GOLD },
  { name: "Advanced Excel", level: 93, color: TEAL },
  { name: "SAP MM/SRM", level: 84, color: NAVY_LIGHT },
  { name: "Data Modeling", level: 90, color: "#3a9e8a" },
  { name: "KPI Dashboards", level: 95, color: GOLD },
  { name: "Power Query", level: 91, color: TEAL },
  { name: "Forecasting", level: 86, color: NAVY_LIGHT },
  { name: "ESG/BRSR", level: 80, color: "#3a9e8a" },
];

const experiences = [
  {
    company: "Rua Al Madinah Holding",
    location: "Madinah, Saudi Arabia",
    role: "Power BI Expert / Planning & Reporting Specialist",
    period: "Feb 2024 – Present",
    current: true,
    achievements: [
      "Develop & maintain 15+ interactive Power BI dashboards for strategic KPIs",
      "Built Project Procurement Delay Analysis dashboard → Outstanding Performance Award 2024",
      "Automate reporting pipelines improving efficiency across departments",
      "Present data-backed strategies to Executive Director & leadership",
    ],
  },
  {
    company: "Sansera Engineering Limited",
    location: "India",
    role: "Assistant Manager Procurement – Aluminum & Raw Materials",
    period: "Jun 2023 – Feb 2024",
    current: false,
    achievements: [
      "Spearheaded aluminum procurement using Power BI and SAP ERP",
      "Managed IATF 16949 & ESG compliance across supplier base",
      "Developed Power BI spend analytics & supplier scorecard reports",
    ],
  },
  {
    company: "Schueco India Pvt. Ltd.",
    location: "India",
    role: "Assistant Manager Procurement – Aluminium Extrusion & Die Development",
    period: "May 2019 – May 2023",
    current: false,
    achievements: [
      "Drove end-to-end procurement & die development for aluminum extrusion profiles",
      "Deployed Power BI for real-time supplier performance — improved on-time delivery by 20%",
      "Managed 30+ supplier portfolio with annual rate contracts",
    ],
  },
  {
    company: "Hyundai Steel India",
    location: "India",
    role: "Demand Planning & Procurement Analyst",
    period: "May 2018 – May 2019",
    current: false,
    achievements: [
      "Led steel coil procurement for KIA production lines",
      "Data-driven inventory forecasting and safety stock optimization",
    ],
  },
  {
    company: "Aradhya Steel Pvt. Ltd.",
    location: "India",
    role: "Senior Engineer Procurement – Steel Wire Rods",
    period: "Mar 2017 – Apr 2018",
    current: false,
    achievements: [
      "End-to-end steel wire rod procurement & inventory management",
      "Established supplier evaluation frameworks & procurement dashboards",
    ],
  },
  {
    company: "Mactech Engineering",
    location: "India",
    role: "Procurement and QA Engineer",
    period: "Feb 2016 – Mar 2017",
    current: false,
    achievements: [
      "OpEx sourcing & supplier development for engineering components",
      "QMS implementation per ISO standards",
    ],
  },
  {
    company: "CIE Automotive India Ltd.",
    location: "India",
    role: "Casting Procurement & Quality Engineer",
    period: "Jun 2012 – Jul 2015",
    current: false,
    achievements: [
      "Led casting procurement & outsourcing of special processes",
      "Implemented QMS per IATF 16949 — contributed to annual cost savings",
    ],
  },
];

const competencies = [
  { icon: <BarChart2 size={20} />, label: "Power BI Dashboard Development" },
  { icon: <TrendingUp size={20} />, label: "Data Analytics & Visualization" },
  { icon: <Briefcase size={20} />, label: "Procurement & Sourcing Strategy" },
  { icon: <Database size={20} />, label: "SAP ERP (MM/SRM Modules)" },
  { icon: <TrendingUp size={20} />, label: "Supply Chain Optimization" },
  { icon: <CheckCircle size={20} />, label: "Inventory & Demand Planning" },
  { icon: <Star size={20} />, label: "ESG / BRSR Compliance" },
  { icon: <CheckCircle size={20} />, label: "IATF 16949 & ISO Standards" },
  { icon: <BarChart2 size={20} />, label: "KPI Design & Performance Tracking" },
  { icon: <Users size={20} />, label: "Stakeholder Reporting" },
  { icon: <Database size={20} />, label: "Microsoft Excel (Advanced)" },
  { icon: <Users size={20} />, label: "Supplier Audit & Management" },
];

const education = [
  {
    institution: "Swiss International Management Academy (SIMA)",
    degree: "Doctor of Business Administration (DBA)",
    period: "2025 – Present",
    detail: "Reg: P25/07/6/ED1853 | Research Focus: Data Analytics & Business Strategy",
    badge: "In Progress",
  },
  {
    institution: "Indian Institute of Import-Export Management (PIIEM)",
    degree: "Post Graduate Diploma in Import-Export Management",
    period: "Feb 2017 – Feb 2018",
    detail: "",
    badge: "Completed",
  },
  {
    institution: "Anna University, India",
    degree: "Bachelor of Engineering – Mechanical Engineering (BE)",
    period: "Jul 2007 – Apr 2011",
    detail: "Best Outstanding Student Award | Distinction in Curriculum, Sports & Extracurriculars",
    badge: "Distinction",
  },
];

const languages = [
  { lang: "English", level: 100, tag: "Native/Fluent" },
  { lang: "Tamil", level: 100, tag: "Native" },
  { lang: "Hindi", level: 90, tag: "Fluent" },
  { lang: "Telugu", level: 85, tag: "Fluent" },
  { lang: "Kannada", level: 85, tag: "Fluent" },
  { lang: "Malayalam", level: 82, tag: "Fluent" },
  { lang: "Arabic", level: 55, tag: "Intermediate" },
  { lang: "German", level: 25, tag: "Basic" },
];

const projects = [
  {
    title: "Project Procurement Delay Analysis Dashboard",
    org: "Rua Al Madinah Holding",
    year: "2024",
    impact: "Outstanding Performance Award 2024",
    desc: "Dynamic end-to-end delay pattern analysis dashboard delivering executive-level insights to the Executive Director, enabling strategic procurement decisions.",
    tags: ["Power BI", "DAX", "Procurement Analytics", "Executive Reporting"],
    highlight: true,
  },
  {
    title: "Supplier Scorecard & Spend Analytics System",
    org: "Sansera Engineering Limited",
    year: "2023",
    impact: "20% Supplier Performance Improvement",
    desc: "Comprehensive Power BI-driven supplier scorecard with IATF 16949 compliance tracking, spend analytics, and corrective action workflows for 30+ suppliers.",
    tags: ["Power BI", "SAP MM", "IATF 16949", "Spend Analytics"],
    highlight: false,
  },
  {
    title: "Real-Time Procurement Performance Dashboard",
    org: "Schueco India Pvt. Ltd.",
    year: "2019–2023",
    impact: "20% Reduction in Lead Times",
    desc: "Real-time supplier performance, cost trend, and procurement efficiency dashboard. Reduced lead times by 20% and improved on-time delivery through data-driven supplier negotiations.",
    tags: ["Power BI", "Aluminium Procurement", "Cost Analytics", "Supplier KPIs"],
    highlight: false,
  },
];

/* ─── ANIMATIONS ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

/* ─── CUSTOM TOOLTIP ─── */
interface TooltipPayloadItem {
  name: string;
  value: number;
}
interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}
const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: NAVY, border: `1px solid ${GOLD}`, borderRadius: 8, padding: "8px 14px" }}>
        <p style={{ color: GOLD, fontSize: 12, fontWeight: 600 }}>{label}</p>
        <p style={{ color: "#fff", fontSize: 13 }}>{payload[0].value}%</p>
      </div>
    );
  }
  return null;
};

/* ─── MAIN COMPONENT ─── */
const Index = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const navLinks = ["about", "skills", "experience", "projects", "education", "contact"];

  return (
    <div className="min-h-screen" style={{ background: "#f0f2f8", fontFamily: "'Inter', sans-serif" }}>
      {/* ── NAVBAR ── */}
      {/* @section: navbar */}
      <nav
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          background: scrolled ? `rgba(30,42,74,0.97)` : "transparent",
          backdropFilter: scrolled ? "blur(12px)" : "none",
          borderBottom: scrolled ? `1px solid rgba(201,151,43,0.3)` : "none",
          transition: "all 0.3s ease",
          padding: "0 24px",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <span style={{ color: GOLD, fontWeight: 800, fontSize: 18, letterSpacing: 1 }}>PAULRAJ</span>
          {/* Desktop nav */}
          <div className="hidden md:flex" style={{ gap: 28 }}>
            {navLinks.map((link) => (
              <button
                key={link}
                onClick={() => scrollTo(link)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: scrolled ? "#e8edf5" : NAVY, fontWeight: 500,
                  fontSize: 14, textTransform: "capitalize", letterSpacing: 0.5,
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.color = GOLD; }}
                onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.color = scrolled ? "#e8edf5" : NAVY; }}
              >
                {link}
              </button>
            ))}
            <a
              href="mailto:manomajor@gmail.com"
              style={{
                background: GOLD, color: NAVY, padding: "7px 18px", borderRadius: 6,
                fontWeight: 700, fontSize: 13, textDecoration: "none",
              }}
            >
              Hire Me
            </a>
          </div>
          {/* Mobile menu toggle */}
          <button
            className="md:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ background: "none", border: "none", cursor: "pointer", color: scrolled ? "#fff" : NAVY }}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {/* Mobile dropdown */}
        {menuOpen && (
          <div style={{ background: NAVY, padding: "16px 24px 24px" }}>
            {navLinks.map((link) => (
              <button
                key={link}
                onClick={() => scrollTo(link)}
                style={{
                  display: "block", background: "none", border: "none", cursor: "pointer",
                  color: "#e8edf5", fontWeight: 500, fontSize: 15, textTransform: "capitalize",
                  padding: "10px 0", width: "100%", textAlign: "left",
                }}
              >
                {link}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      {/* @section: hero */}
      <section style={{
        minHeight: "100vh", background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 60%, #1a3550 100%)`,
        display: "flex", alignItems: "center", position: "relative", overflow: "hidden",
        paddingTop: 64,
      }}>
        {/* Decorative grid */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.05,
          backgroundImage: "linear-gradient(rgba(201,151,43,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(201,151,43,0.8) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }} />
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 24px", width: "100%", position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 48, alignItems: "center" }}>
            {/* Portrait */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              style={{ flexShrink: 0 }}
            >
              <div style={{
                width: 220, height: 220, borderRadius: "50%", overflow: "hidden",
                border: `4px solid ${GOLD}`,
                boxShadow: `0 0 0 8px rgba(201,151,43,0.15), 0 24px 60px rgba(0,0,0,0.5)`,
              }}>
                <img src={PORTRAIT} alt="Manoharan Paulraj" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            </motion.div>

            {/* Info */}
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
              style={{ flex: 1, minWidth: 280 }}
            >
              <motion.div variants={fadeUp}>
                <span style={{
                  background: `rgba(201,151,43,0.2)`, color: GOLD, border: `1px solid rgba(201,151,43,0.4)`,
                  borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 600, letterSpacing: 1,
                  textTransform: "uppercase",
                }}>
                  ⭐ Outstanding Performance Award 2024
                </span>
              </motion.div>
              <motion.h1 variants={fadeUp} style={{ color: "#fff", fontSize: "clamp(2rem, 5vw, 3.2rem)", fontWeight: 800, marginTop: 12, marginBottom: 4, lineHeight: 1.15 }}>
                Manoharan<br />
                <span style={{ color: GOLD }}>Paulraj</span>
              </motion.h1>
              <motion.p variants={fadeUp} style={{ color: "#94a3c4", fontSize: 18, fontWeight: 500, marginBottom: 8 }}>
                Power BI Expert & Data Analytics Specialist
              </motion.p>
              <motion.p variants={fadeUp} style={{ color: "#7a8db0", fontSize: 14, marginBottom: 24, display: "flex", alignItems: "center", gap: 6 }}>
                <MapPin size={14} /> Madinah, Saudi Arabia &nbsp;|&nbsp; 12+ Years Experience &nbsp;|&nbsp; DBA Candidate
              </motion.p>

              {/* Stats */}
              <motion.div variants={fadeUp} style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 28 }}>
                {[
                  { label: "Years Experience", value: "12+" },
                  { label: "Power BI Dashboards", value: "15+" },
                  { label: "Industries Served", value: "4+" },
                  { label: "Awards Won", value: "2" },
                ].map((s) => (
                  <div key={s.label} style={{
                    background: "rgba(255,255,255,0.07)", border: `1px solid rgba(201,151,43,0.25)`,
                    borderRadius: 10, padding: "12px 20px", textAlign: "center", minWidth: 100,
                  }}>
                    <div style={{ color: GOLD, fontSize: 22, fontWeight: 800 }}>{s.value}</div>
                    <div style={{ color: "#94a3c4", fontSize: 11, marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </motion.div>

              {/* Contact & CTA */}
              <motion.div variants={fadeUp} style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                <a href="mailto:manomajor@gmail.com" style={{
                  display: "flex", alignItems: "center", gap: 7,
                  background: GOLD, color: NAVY, borderRadius: 8, padding: "10px 20px",
                  fontWeight: 700, fontSize: 14, textDecoration: "none",
                }}>
                  <Mail size={15} /> Get In Touch
                </a>
                <a href="https://linkedin.com/in/manoharan-paulraj-bb3575126" target="_blank" rel="noreferrer" style={{
                  display: "flex", alignItems: "center", gap: 7,
                  background: "rgba(255,255,255,0.1)", color: "#e8edf5", borderRadius: 8, padding: "10px 20px",
                  fontWeight: 600, fontSize: 14, textDecoration: "none", border: "1px solid rgba(255,255,255,0.2)",
                }}>
                  <FaLinkedin size={15} /> LinkedIn
                </a>
                <a href="tel:+966550370366" style={{
                  display: "flex", alignItems: "center", gap: 7,
                  background: "rgba(255,255,255,0.1)", color: "#e8edf5", borderRadius: 8, padding: "10px 20px",
                  fontWeight: 600, fontSize: 14, textDecoration: "none", border: "1px solid rgba(255,255,255,0.2)",
                }}>
                  <Phone size={15} /> +966 550 370 366
                </a>
              </motion.div>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ textAlign: "center", marginTop: 48, cursor: "pointer" }}
            onClick={() => scrollTo("about")}
          >
            <ChevronDown style={{ color: GOLD }} size={28} />
          </motion.div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      {/* @section: about */}
      <section id="about" style={{ padding: "80px 24px", background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={fadeUp} style={{ textAlign: "center", marginBottom: 48 }}>
              <span style={{ color: GOLD, fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 2 }}>Professional Summary</span>
              <h2 style={{ color: NAVY, fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: 800, marginTop: 8 }}>About Paul Raj</h2>
            </motion.div>
            <motion.div variants={fadeUp} style={{
              background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`,
              borderRadius: 16, padding: "36px 40px",
              borderLeft: `5px solid ${GOLD}`,
              boxShadow: "0 12px 40px rgba(30,42,74,0.15)",
            }}>
              <p style={{ color: "#cbd5e8", fontSize: 16, lineHeight: 1.8, margin: 0 }}>
                Results-driven <strong style={{ color: GOLD }}>Power BI Expert and Data Analytics Specialist</strong> with <strong style={{ color: "#fff" }}>12+ years of progressive experience</strong> across procurement, supply chain management, inventory control, demand planning, and quality assurance in manufacturing and infrastructure sectors. Proven expertise in designing and deploying interactive Power BI dashboards that translate complex datasets into actionable insights, driving operational excellence and strategic decision-making.
              </p>
              <p style={{ color: "#cbd5e8", fontSize: 16, lineHeight: 1.8, margin: "16px 0 0" }}>
                Adept at aligning analytics with business goals, <strong style={{ color: "#fff" }}>ESG/BRSR compliance</strong>, <strong style={{ color: "#fff" }}>IATF 16949 standards</strong>, and continuous improvement initiatives. Currently completing a <strong style={{ color: GOLD }}>DBA at Swiss International Management Academy</strong>. Recognized with <strong style={{ color: GOLD }}>Outstanding Performance Award 2024</strong>.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── COMPETENCIES ── */}
      {/* @section: competencies */}
      <section id="skills" style={{ padding: "80px 24px", background: "#f0f2f8" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={fadeUp} style={{ textAlign: "center", marginBottom: 48 }}>
              <span style={{ color: GOLD, fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 2 }}>What I Bring</span>
              <h2 style={{ color: NAVY, fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: 800, marginTop: 8 }}>Core Competencies</h2>
            </motion.div>
            <motion.div variants={stagger} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16, marginBottom: 60 }}>
              {competencies.map((c, i) => (
                <motion.div key={i} variants={fadeUp}
                  style={{
                    background: "#fff", borderRadius: 12, padding: "18px 20px",
                    display: "flex", alignItems: "center", gap: 14,
                    border: `1px solid rgba(30,42,74,0.12)`,
                    boxShadow: "0 2px 8px rgba(30,42,74,0.06)",
                    transition: "all 0.2s",
                  }}
                  whileHover={{ scale: 1.02, boxShadow: `0 6px 20px rgba(30,42,74,0.15)`, borderColor: GOLD }}
                >
                  <div style={{ color: GOLD, flexShrink: 0 }}>{c.icon}</div>
                  <span style={{ color: NAVY, fontWeight: 600, fontSize: 13.5 }}>{c.label}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* ── SKILLS VISUALIZATION ── */}
            {/* @section: skills-charts */}
            <motion.div variants={fadeUp} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 32 }}>
              {/* Radar */}
              <div style={{ background: "#fff", borderRadius: 16, padding: "28px 24px", boxShadow: "0 4px 20px rgba(30,42,74,0.08)" }}>
                <h3 style={{ color: NAVY, fontWeight: 700, fontSize: 16, marginBottom: 20, textAlign: "center" }}>Skill Competency Radar</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={skillsRadar} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                    <PolarGrid stroke="rgba(30,42,74,0.12)" />
                    <PolarAngleAxis dataKey="skill" tick={{ fill: NAVY, fontSize: 11, fontWeight: 600 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#94a3c4", fontSize: 9 }} />
                    <Radar name="Proficiency" dataKey="score" stroke={GOLD} fill={GOLD} fillOpacity={0.25} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              {/* Bar chart */}
              <div style={{ background: "#fff", borderRadius: 16, padding: "28px 24px", boxShadow: "0 4px 20px rgba(30,42,74,0.08)" }}>
                <h3 style={{ color: NAVY, fontWeight: 700, fontSize: 16, marginBottom: 20, textAlign: "center" }}>Technical Proficiency (%)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={technicalBars} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 4 }}>
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: "#94a3c4", fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fill: NAVY, fontSize: 11, fontWeight: 500 }} width={110} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="level" radius={[0, 4, 4, 0]}>
                      {technicalBars.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── EXPERIENCE ── */}
      {/* @section: experience */}
      <section id="experience" style={{ padding: "80px 24px", background: "#fff" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={fadeUp} style={{ textAlign: "center", marginBottom: 52 }}>
              <span style={{ color: GOLD, fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 2 }}>Career Journey</span>
              <h2 style={{ color: NAVY, fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: 800, marginTop: 8 }}>Professional Experience</h2>
            </motion.div>
            {/* Timeline */}
            <div style={{ position: "relative" }}>
              {/* Vertical line */}
              <div style={{
                position: "absolute", left: 20, top: 0, bottom: 0, width: 2,
                background: `linear-gradient(to bottom, ${GOLD}, rgba(201,151,43,0.1))`,
              }} />
              {experiences.map((exp, i) => (
                <motion.div key={i} variants={fadeUp} style={{ paddingLeft: 56, paddingBottom: 36, position: "relative" }}>
                  {/* Dot */}
                  <div style={{
                    position: "absolute", left: 11, top: 4, width: 20, height: 20, borderRadius: "50%",
                    background: exp.current ? GOLD : NAVY_LIGHT,
                    border: `3px solid ${exp.current ? "#fff" : "rgba(201,151,43,0.3)"}`,
                    boxShadow: exp.current ? `0 0 0 4px rgba(201,151,43,0.25)` : "none",
                    zIndex: 1,
                  }} />
                  <div style={{
                    background: exp.current ? `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` : "#f8f9fc",
                    borderRadius: 12, padding: "20px 24px",
                    border: `1px solid ${exp.current ? "rgba(201,151,43,0.35)" : "rgba(30,42,74,0.1)"}`,
                    boxShadow: exp.current ? "0 8px 24px rgba(30,42,74,0.2)" : "0 2px 8px rgba(30,42,74,0.06)",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
                      <h3 style={{ color: exp.current ? "#fff" : NAVY, fontWeight: 700, fontSize: 15, margin: 0 }}>{exp.role}</h3>
                      <span style={{
                        background: exp.current ? `rgba(201,151,43,0.2)` : "rgba(30,42,74,0.1)",
                        color: exp.current ? GOLD : "#5a6b8a",
                        borderRadius: 20, padding: "2px 12px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
                      }}>{exp.period}</span>
                    </div>
                    <p style={{ color: exp.current ? GOLD : TEAL, fontWeight: 600, fontSize: 13, marginBottom: 10 }}>
                      {exp.company} &nbsp;·&nbsp; <span style={{ color: exp.current ? "#94a3c4" : "#7a8db0", fontWeight: 400 }}>{exp.location}</span>
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {exp.achievements.map((a, j) => (
                        <li key={j} style={{ color: exp.current ? "#cbd5e8" : "#4a5568", fontSize: 13.5, lineHeight: 1.7 }}>{a}</li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── PROJECTS ── */}
      {/* @section: projects */}
      <section id="projects" style={{ padding: "80px 24px", background: "#f0f2f8" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={fadeUp} style={{ textAlign: "center", marginBottom: 48 }}>
              <span style={{ color: GOLD, fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 2 }}>Key Projects</span>
              <h2 style={{ color: NAVY, fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: 800, marginTop: 8 }}>Achievements & Projects</h2>
            </motion.div>
            <motion.div variants={stagger} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
              {projects.map((p, i) => (
                <motion.div key={i} variants={fadeUp}
                  style={{
                    background: p.highlight ? `linear-gradient(145deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` : "#fff",
                    borderRadius: 16, padding: "28px 28px 24px",
                    border: `1px solid ${p.highlight ? "rgba(201,151,43,0.4)" : "rgba(30,42,74,0.1)"}`,
                    boxShadow: p.highlight ? "0 12px 36px rgba(30,42,74,0.25)" : "0 4px 16px rgba(30,42,74,0.07)",
                    position: "relative", overflow: "hidden",
                  }}
                  whileHover={{ scale: 1.02 }}
                >
                  {p.highlight && (
                    <div style={{
                      position: "absolute", top: 16, right: 16,
                      background: GOLD, color: NAVY, borderRadius: 20,
                      padding: "3px 12px", fontSize: 11, fontWeight: 700,
                    }}>⭐ Award Winning</div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <BarChart2 size={18} color={GOLD} />
                    <span style={{ color: p.highlight ? GOLD : TEAL, fontWeight: 600, fontSize: 12 }}>{p.org} · {p.year}</span>
                  </div>
                  <h3 style={{ color: p.highlight ? "#fff" : NAVY, fontWeight: 700, fontSize: 16, marginBottom: 10, lineHeight: 1.4 }}>{p.title}</h3>
                  <p style={{ color: p.highlight ? "#94a3c4" : "#5a6b8a", fontSize: 13.5, lineHeight: 1.7, marginBottom: 16 }}>{p.desc}</p>
                  <div style={{
                    background: p.highlight ? "rgba(201,151,43,0.15)" : "rgba(30,42,74,0.06)",
                    borderRadius: 8, padding: "8px 14px", marginBottom: 16,
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                    <TrendingUp size={14} color={GOLD} />
                    <span style={{ color: GOLD, fontWeight: 700, fontSize: 13 }}>{p.impact}</span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {p.tags.map((tag, j) => (
                      <span key={j} style={{
                        background: p.highlight ? "rgba(255,255,255,0.1)" : "rgba(30,42,74,0.08)",
                        color: p.highlight ? "#94a3c4" : "#4a5880",
                        border: `1px solid ${p.highlight ? "rgba(255,255,255,0.15)" : "rgba(30,42,74,0.15)"}`,
                        borderRadius: 4, padding: "3px 10px", fontSize: 11, fontWeight: 500,
                        fontFamily: "'JetBrains Mono', monospace",
                      }}>{tag}</span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── EDUCATION ── */}
      {/* @section: education */}
      <section id="education" style={{ padding: "80px 24px", background: "#fff" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={fadeUp} style={{ textAlign: "center", marginBottom: 48 }}>
              <span style={{ color: GOLD, fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 2 }}>Academic Background</span>
              <h2 style={{ color: NAVY, fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: 800, marginTop: 8 }}>Education</h2>
            </motion.div>
            <motion.div variants={stagger} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {education.map((e, i) => (
                <motion.div key={i} variants={fadeUp}
                  style={{
                    background: "#f8f9fc", borderRadius: 14, padding: "24px 28px",
                    display: "flex", gap: 20, alignItems: "flex-start",
                    border: "1px solid rgba(30,42,74,0.1)",
                    boxShadow: "0 2px 8px rgba(30,42,74,0.05)",
                  }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: 12, background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <BookOpen size={20} color={GOLD} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
                      <h3 style={{ color: NAVY, fontWeight: 700, fontSize: 15, margin: 0 }}>{e.degree}</h3>
                      <span style={{
                        background: e.badge === "In Progress" ? "rgba(201,151,43,0.15)" : "rgba(42,122,140,0.15)",
                        color: e.badge === "In Progress" ? GOLD : TEAL,
                        border: `1px solid ${e.badge === "In Progress" ? "rgba(201,151,43,0.3)" : "rgba(42,122,140,0.3)"}`,
                        borderRadius: 20, padding: "2px 12px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                      }}>{e.badge}</span>
                    </div>
                    <p style={{ color: TEAL, fontWeight: 600, fontSize: 13, margin: "0 0 4px" }}>{e.institution}</p>
                    <p style={{ color: "#7a8db0", fontSize: 13, margin: "0 0 4px" }}>{e.period}</p>
                    {e.detail && <p style={{ color: "#94a3c4", fontSize: 12.5, margin: 0 }}>{e.detail}</p>}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── AWARDS ── */}
      {/* @section: awards */}
      <section style={{ padding: "80px 24px", background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={fadeUp} style={{ textAlign: "center", marginBottom: 48 }}>
              <span style={{ color: GOLD, fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 2 }}>Recognition</span>
              <h2 style={{ color: "#fff", fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: 800, marginTop: 8 }}>Awards & Honors</h2>
            </motion.div>
            <motion.div variants={stagger} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 24 }}>
              {[
                {
                  title: "Outstanding Performance Award 2024",
                  org: "Rua Al Madinah Holding Company",
                  desc: "Recognized for consistently exceeding Executive Director expectations, delivering results on schedule, and creating the high-impact Project Procurement Delay Analysis Dynamic Dashboard.",
                },
                {
                  title: "Best Outstanding Student Award",
                  org: "Anna University (2007–2011)",
                  desc: "Honored for exceptional performance in academics, sports, and extracurricular activities during the Bachelor of Engineering program.",
                },
              ].map((award, i) => (
                <motion.div key={i} variants={fadeUp}
                  style={{
                    background: "rgba(255,255,255,0.07)", borderRadius: 16, padding: "28px",
                    border: `1px solid rgba(201,151,43,0.35)`,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                  }}
                >
                  <Award size={32} color={GOLD} style={{ marginBottom: 14 }} />
                  <h3 style={{ color: "#fff", fontWeight: 700, fontSize: 17, marginBottom: 6 }}>{award.title}</h3>
                  <p style={{ color: GOLD, fontWeight: 600, fontSize: 13, marginBottom: 10 }}>{award.org}</p>
                  <p style={{ color: "#94a3c4", fontSize: 13.5, lineHeight: 1.7, margin: 0 }}>{award.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── LANGUAGES ── */}
      {/* @section: languages */}
      <section style={{ padding: "80px 24px", background: "#f0f2f8" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={fadeUp} style={{ textAlign: "center", marginBottom: 48 }}>
              <span style={{ color: GOLD, fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 2 }}>Communication</span>
              <h2 style={{ color: NAVY, fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: 800, marginTop: 8 }}>
                <Globe size={28} color={TEAL} style={{ verticalAlign: "middle", marginRight: 10 }} />
                Languages
              </h2>
            </motion.div>
            <motion.div variants={stagger} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 20 }}>
              {languages.map((l, i) => (
                <motion.div key={i} variants={fadeUp}
                  style={{ background: "#fff", borderRadius: 12, padding: "18px 22px", boxShadow: "0 2px 8px rgba(30,42,74,0.06)", border: "1px solid rgba(30,42,74,0.08)" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ color: NAVY, fontWeight: 700, fontSize: 14 }}>{l.lang}</span>
                    <span style={{
                      background: l.level === 100 ? "rgba(201,151,43,0.15)" : "rgba(42,122,140,0.12)",
                      color: l.level === 100 ? GOLD : TEAL,
                      borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600,
                    }}>{l.tag}</span>
                  </div>
                  <div style={{ height: 6, background: "rgba(30,42,74,0.1)", borderRadius: 3, overflow: "hidden" }}>
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${l.level}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: i * 0.08, ease: "easeOut" }}
                      style={{
                        height: "100%", borderRadius: 3,
                        background: l.level >= 90 ? `linear-gradient(90deg, ${NAVY_LIGHT}, ${GOLD})` : `linear-gradient(90deg, ${TEAL}, ${NAVY_LIGHT})`,
                      }}
                    />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      {/* @section: contact */}
      <section id="contact" style={{ padding: "80px 24px", background: "#fff" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={fadeUp}>
              <span style={{ color: GOLD, fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 2 }}>Let's Connect</span>
              <h2 style={{ color: NAVY, fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: 800, marginTop: 8, marginBottom: 16 }}>Ready to Work Together?</h2>
              <p style={{ color: "#5a6b8a", fontSize: 15, lineHeight: 1.7, maxWidth: 560, margin: "0 auto 40px" }}>
                I'm actively seeking new opportunities in Power BI, Data Analytics, and Procurement Management. Let's discuss how I can add value to your organization.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 16, marginBottom: 40 }}>
              {[
                { icon: <Mail size={18} />, label: "manomajor@gmail.com", href: "mailto:manomajor@gmail.com" },
                { icon: <Phone size={18} />, label: "+966 550 370 366", href: "tel:+966550370366" },
                { icon: <FaLinkedin size={18} />, label: "LinkedIn Profile", href: "https://linkedin.com/in/manoharan-paulraj-bb3575126" },
              ].map((c, i) => (
                <a key={i} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    background: "#f0f2f8", color: NAVY, borderRadius: 10, padding: "14px 24px",
                    fontWeight: 600, fontSize: 14, textDecoration: "none",
                    border: "1px solid rgba(30,42,74,0.12)",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLAnchorElement;
                    el.style.background = NAVY; el.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLAnchorElement;
                    el.style.background = "#f0f2f8"; el.style.color = NAVY;
                  }}
                >
                  <span style={{ color: GOLD }}>{c.icon}</span>
                  {c.label}
                  <ExternalLink size={12} style={{ opacity: 0.5 }} />
                </a>
              ))}
            </motion.div>
            <motion.div variants={fadeUp}>
              <a href="mailto:manomajor@gmail.com" style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`,
                color: "#fff", borderRadius: 10, padding: "16px 36px",
                fontWeight: 700, fontSize: 16, textDecoration: "none",
                boxShadow: `0 8px 24px rgba(30,42,74,0.3)`,
              }}>
                <Mail size={18} color={GOLD} /> Send Me an Email
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      {/* @section: footer */}
      <footer style={{
        background: NAVY, padding: "28px 24px", textAlign: "center",
        borderTop: `2px solid ${GOLD}`,
      }}>
        <p style={{ color: "#7a8db0", fontSize: 13, margin: 0 }}>
          © 2024 <strong style={{ color: GOLD }}>Manoharan Paulraj</strong> · Power BI Expert & Data Analytics Specialist · Madinah, Saudi Arabia
        </p>
        <p style={{ color: "#4a5880", fontSize: 12, marginTop: 6, marginBottom: 0 }}>
          ATS-Optimized Professional Portfolio · Open to Global Opportunities
        </p>
      </footer>
    </div>
  );
};

export default Index;
